import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getItem,
  getItemVendors,
  listVendors,
  createVendorItem,
  updateItem,
  getItemStatuses,
} from '../../../endpoints/chapterSupplies';
import type {
  CreateVendorItemRequest,
  UpdateItemRequest,
} from '../../../types/chapterSupplies';

export default function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateItemRequest>({});
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [addVendorForm, setAddVendorForm] = useState<{
    vendor_id: string;
    vendor_sku: string | null;
    cost: number | null;
    lead_time_days: number | null;
    is_preferred: number;
  }>({
    vendor_id: '',
    vendor_sku: null,
    cost: null,
    lead_time_days: null,
    is_preferred: 0,
  });
  const [addVendorError, setAddVendorError] = useState<string | null>(null);

  const {
    data: item,
    isFetching,
    refetch: refetchItem,
  } = useApiGet(['chapter-supplies-item', id], () => getItem(id!), {
    enabled: !!id,
    retry: 1,
  });

  const { data: statuses = [] } = useApiGet(
    ['chapter-supplies-statuses-items'],
    getItemStatuses,
    { enabled: true, retry: 1 }
  );

  const { mutate: doUpdateItem, isPending: isUpdatingItem } = useApiSend(
    (payload: UpdateItemRequest) => updateItem(id!, payload),
    () => {
      setEditError(null);
      setEditing(false);
      refetchItem();
    },
    (err) => {
      setEditError(err?.message ?? 'Update failed');
    },
    [['chapter-supplies-item', id]]
  );

  const { data: itemVendorsData } = useApiGet(
    ['chapter-supplies-item-vendors', id],
    () => getItemVendors(id!),
    { enabled: !!id, retry: 1 }
  );

  const { data: vendorsData } = useApiGet(
    ['chapter-supplies-vendors-for-item', 500],
    () => listVendors({ limit: 500, offset: 0 }),
    { enabled: addVendorOpen && !!id, retry: 1 }
  );

  const itemVendors = itemVendorsData?.vendors ?? [];
  const allVendors = vendorsData?.vendors ?? [];
  const existingVendorIds = new Set(itemVendors.map((v) => v.vendor_id));
  const vendorOptions = allVendors.filter((v) => !existingVendorIds.has(v.id));

  const { mutate: submitVendorItem, isPending: isAddingVendor } = useApiSend(
    (params: { vendorId: string; data: CreateVendorItemRequest }) =>
      createVendorItem(params.vendorId, params.data),
    () => {
      setAddVendorOpen(false);
      setAddVendorForm({
        vendor_id: '',
        vendor_sku: null,
        cost: null,
        lead_time_days: null,
        is_preferred: 0,
      });
      setAddVendorError(null);
    },
    (err) => {
      setAddVendorError(err?.message ?? 'Failed to add vendor');
    },
    [
      ['chapter-supplies-item-vendors', id],
      ['chapter-supplies-item', id],
    ]
  );

  const handleOpenAddVendor = () => {
    setAddVendorError(null);
    setAddVendorForm({
      vendor_id: '',
      vendor_sku: null,
      cost: null,
      lead_time_days: null,
      is_preferred: 0,
    });
    setAddVendorOpen(true);
  };

  const handleCloseAddVendor = () => {
    setAddVendorOpen(false);
    setAddVendorError(null);
  };

  const handleAddVendorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !addVendorForm.vendor_id?.trim()) {
      setAddVendorError('Vendor is required');
      return;
    }
    const payload: CreateVendorItemRequest = {
      item_id: id,
      vendor_sku: addVendorForm.vendor_sku?.trim() || null,
      cost: addVendorForm.cost != null ? Number(addVendorForm.cost) : null,
      lead_time_days:
        addVendorForm.lead_time_days != null
          ? Number(addVendorForm.lead_time_days)
          : null,
      is_preferred: addVendorForm.is_preferred ? 1 : 0,
    };
    submitVendorItem({
      vendorId: addVendorForm.vendor_id.trim(),
      data: payload,
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.name?.trim()) {
      setEditError('Name is required');
      return;
    }
    const payload: UpdateItemRequest = {
      name: editForm.name.trim(),
      item_status_id: editForm.item_status_id?.trim() || undefined,
      description: editForm.description?.trim() || null,
      sku: editForm.sku?.trim() || null,
      reorder_point:
        editForm.reorder_point != null ? editForm.reorder_point : null,
      weight: editForm.weight != null ? editForm.weight : null,
      length: editForm.length != null ? editForm.length : null,
      width: editForm.width != null ? editForm.width : null,
      height: editForm.height != null ? editForm.height : null,
    };
    doUpdateItem(payload);
  };

  if (!id) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography>Invalid item ID.</Typography>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title={item?.name ?? 'Item'}
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management/items')}
            >
              Back to list
            </Button>
          }
          rightDom={
            editing ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setEditing(false);
                  setEditError(null);
                }}
              >
                Cancel
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  disabled={!item}
                  onClick={() => {
                    if (!item) return;
                    setEditing(true);
                    setEditError(null);
                    setEditForm({
                      name: item.name ?? undefined,
                      item_status_id: item.item_status_id ?? undefined,
                      description: item.description ?? null,
                      sku: item.sku ?? null,
                      reorder_point: item.reorder_point ?? null,
                      weight: item.weight ?? null,
                      length: item.length ?? null,
                      width: item.width ?? null,
                      height: item.height ?? null,
                    });
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenAddVendor}
                >
                  Add vendor
                </Button>
              </Box>
            )
          }
          subtitle={undefined}
        />
      </Grid>
      {isFetching && (
        <Grid item xs={12}>
          <Typography>Loading…</Typography>
        </Grid>
      )}
      {!isFetching && item && (
        <>
          <Grid item xs={12} sx={{ mt: 2 }}>
            {editing ? (
              <Paper sx={{ p: 2 }}>
                <form onSubmit={handleEditSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Name"
                        value={editForm.name ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        error={!!editError && !editForm.name?.trim()}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="item-edit-status-label">
                          Status
                        </InputLabel>
                        <Select
                          labelId="item-edit-status-label"
                          label="Status"
                          value={editForm.item_status_id ?? ''}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              item_status_id: e.target.value || undefined,
                            }))
                          }
                        >
                          <MenuItem value="">Draft (default)</MenuItem>
                          {statuses.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Description"
                        value={editForm.description ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            description: e.target.value || null,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="SKU"
                        value={editForm.sku ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            sku: e.target.value || null,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        label="Reorder point"
                        value={editForm.reorder_point ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            reorder_point:
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        label="Weight"
                        value={editForm.weight ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            weight:
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        label="Length"
                        value={editForm.length ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            length:
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        label="Width"
                        value={editForm.width ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            width:
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        label="Height"
                        value={editForm.height ?? ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            height:
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                          }))
                        }
                      />
                    </Grid>
                    {editError && (
                      <Grid item xs={12}>
                        <Typography color="error" variant="body2">
                          {editError}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isUpdatingItem || !editForm.name?.trim()}
                      >
                        {isUpdatingItem ? 'Saving…' : 'Save changes'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            ) : (
              <>
                <Typography variant="body1">
                  <strong>Status:</strong>{' '}
                  {item.item_status_name ?? item.item_status_id}
                </Typography>
                {item.sku && (
                  <Typography variant="body1">
                    <strong>SKU:</strong> {item.sku}
                  </Typography>
                )}
                {item.description && (
                  <Typography variant="body1">
                    <strong>Description:</strong> {item.description}
                  </Typography>
                )}
                {item.quantity_on_hand != null && (
                  <Typography variant="body1">
                    <strong>Quantity on hand:</strong> {item.quantity_on_hand}
                  </Typography>
                )}
              </>
            )}
          </Grid>
          {!editing && (
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Vendors
              </Typography>
              {itemVendors.length === 0 ? (
                <Typography color="text.secondary">
                  No vendors linked. Use &quot;Add vendor&quot; to link a vendor
                  that carries this item.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Vendor SKU</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">Lead time (days)</TableCell>
                        <TableCell>Preferred</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemVendors.map((v) => (
                        <TableRow
                          key={v.vendor_item_id}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() =>
                            navigate(
                              `/chapter-supplies-management/vendors/${v.vendor_id}`
                            )
                          }
                        >
                          <TableCell>{v.vendor_name ?? v.vendor_id}</TableCell>
                          <TableCell>{v.vendor_sku ?? '—'}</TableCell>
                          <TableCell align="right">
                            {v.cost != null ? v.cost : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {v.lead_time_days != null ? v.lead_time_days : '—'}
                          </TableCell>
                          <TableCell>{v.is_preferred ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          )}
        </>
      )}

      <Dialog
        open={addVendorOpen}
        onClose={handleCloseAddVendor}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleAddVendorSubmit}>
          <DialogTitle>Add vendor</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={vendorOptions}
                  getOptionLabel={(option) => option.name ?? option.id}
                  value={
                    allVendors.find((v) => v.id === addVendorForm.vendor_id) ??
                    null
                  }
                  onChange={(_, value) =>
                    setAddVendorForm((prev) => ({
                      ...prev,
                      vendor_id: value?.id ?? '',
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendor"
                      required
                      error={!!addVendorError && !addVendorForm.vendor_id}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vendor SKU"
                  value={addVendorForm.vendor_sku ?? ''}
                  onChange={(e) =>
                    setAddVendorForm((prev) => ({
                      ...prev,
                      vendor_sku: e.target.value || null,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  label="Cost"
                  value={addVendorForm.cost ?? ''}
                  onChange={(e) =>
                    setAddVendorForm((prev) => ({
                      ...prev,
                      cost:
                        e.target.value === ''
                          ? null
                          : parseFloat(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                  label="Lead time (days)"
                  value={addVendorForm.lead_time_days ?? ''}
                  onChange={(e) =>
                    setAddVendorForm((prev) => ({
                      ...prev,
                      lead_time_days:
                        e.target.value === ''
                          ? null
                          : parseInt(e.target.value, 10),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!addVendorForm.is_preferred}
                      onChange={(e) =>
                        setAddVendorForm((prev) => ({
                          ...prev,
                          is_preferred: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label="Preferred"
                />
              </Grid>
              {addVendorError && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    {addVendorError}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddVendor}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isAddingVendor || !addVendorForm.vendor_id?.trim()}
            >
              {isAddingVendor ? 'Adding…' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Grid>
  );
}
